import { useRef, useState } from 'react';
import cn from 'classnames';
// Context
import { SocialAccount } from 'context/OnboardingContext';
// Hooks
import { useInitInstagramAuth, useUnlinkSocialMediaConnection } from 'hooks';
// Helpers
import {
  mapSocialPlatformToIcon,
  mapSocialPlatformToLabel,
} from 'helpers/socials';
import { getEnvLink } from 'helpers/routes';
import { formatLargeNumber } from 'helpers/formatLargeNumber';
// Types
import {
  SocialMediaConnectionProvider,
  SocialMediaType,
} from 'api/graphql-global-types';
// Ui
import Text from 'ui3/Text/Text';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Button from 'ui3/Button/Button';
// Components
import SocialAuthorizationDialog from './SocialAuthorizationDialog';
import UnlinkSocialAccountDialog from '../../components/common/UnlinkSocialAccountDialog/UnlinkSocialAccountDialog';
import { showToast } from '../../components/common/Toast/Toast';
// Styles
import styles from './SocialsRowWithIntegration.module.scss';
import { useInitFacebookAuth } from 'hooks/useInitFacebookAuth';
import { useInitTiktokIntegrationAuth } from 'hooks/useInitTiktokIntegrationAuth';

type SocialsRowWithIntegrationProps = {
  socialAccount: SocialAccount;
  isLinked?: boolean;
  loading?: boolean;
  onLinkAccountPress?: () => void | Promise<void>;
  disabled?: boolean;
  redirectUrl?: string;
  showError?: boolean;
};

const SocialsRowWithIntegration = ({
  socialAccount,
  isLinked,
  loading,
  onLinkAccountPress,
  disabled = false,
  redirectUrl: redirectUrlFromProps,
  showError = false,
}: SocialsRowWithIntegrationProps) => {
  const [
    unlinkSocialMediaConnection,
    { loading: isUnlinking },
  ] = useUnlinkSocialMediaConnection();

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(
    false
  );
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const authorizationInProgress = useRef(false);
  const redirectUrl = redirectUrlFromProps || `${getEnvLink()}/dashboard`;

  const [
    initInstagramAuth,
    { loading: isInitInstagramAuth },
  ] = useInitInstagramAuth();
  const [
    initFacebookAuth,
    { loading: isInitFacebookAuth },
  ] = useInitFacebookAuth();
  const [
    initTiktokIntegrationAuth,
    { loading: isInitTiktokAuth },
  ] = useInitTiktokIntegrationAuth();
  const [isUnlinkDialogOpen, setIsUnlinkDialogOpen] = useState(false);
  const [
    pendingUnlinkAccount,
    setPendingUnlinkAccount,
  ] = useState<SocialAccount | null>(null);

  const isConnecting =
    Boolean(loading) ||
    isAuthorizing ||
    isInitInstagramAuth ||
    isInitFacebookAuth ||
    isInitTiktokAuth;
  const isBusy = isConnecting || isUnlinking;
  const isActionDisabled = disabled || isBusy;

  const getInstagramAuthUrl = async () => {
    const { data } = await initInstagramAuth({
      variables: { input: { redirectUrl } },
    });

    return data?.initInstagramAuth?.authUrl;
  };

  const getFacebookAuthUrl = async () => {
    const { data } = await initFacebookAuth({
      variables: { input: { redirectUrl } },
    });

    return data?.initFacebookAuth?.authUrl;
  };

  const getTiktokAuthUrl = async () => {
    const { data } = await initTiktokIntegrationAuth({
      variables: { input: { redirectUrl } },
    });

    return data?.initTiktokIntegrationAuth?.authUrl;
  };

  const getAuthUrl = async () => {
    switch (socialAccount.platform) {
      case SocialMediaType.Instagram:
        return getInstagramAuthUrl();
      case SocialMediaType.Facebook:
        return getFacebookAuthUrl();
      case SocialMediaType.Tiktok:
        return getTiktokAuthUrl();
      default:
        break;
    }
  };

  const onLinkAccountConfirmation = () => {
    setIsConfirmationDialogOpen(true);
  };

  const closeAuthorizationDialog = () => {
    if (!authorizationInProgress.current) setIsConfirmationDialogOpen(false);
  };

  const handleAuthorize = async () => {
    if (authorizationInProgress.current) return;
    authorizationInProgress.current = true;
    setIsAuthorizing(true);
    try {
      const authUrl = await getAuthUrl();
      if (!authUrl)
        throw new Error(
          'Unable to start account authorization. Please try again.'
        );
      await onLinkAccountPress?.();
      window.location.href = authUrl;
      setIsConfirmationDialogOpen(false);
    } catch (error) {
      showToast({
        type: 'error',
        message:
          (error as Error).message ||
          'Unable to link your account. Please try again.',
      });
    } finally {
      authorizationInProgress.current = false;
      setIsAuthorizing(false);
    }
  };

  const handleRemoveLinkedSocialAccount = () => {
    setPendingUnlinkAccount(socialAccount);
    setIsUnlinkDialogOpen(true);
  };

  const handleCloseUnlinkDialog = () => {
    if (isUnlinking) {
      return;
    }

    setIsUnlinkDialogOpen(false);
    setPendingUnlinkAccount(null);
  };

  const handleConfirmUnlink = async () => {
    if (!pendingUnlinkAccount?.platform || !pendingUnlinkAccount?.link) {
      handleCloseUnlinkDialog();
      return;
    }

    setIsUnlinkDialogOpen(false);

    try {
      await unlinkSocialMediaConnection({
        variables: {
          input: {
            provider: (pendingUnlinkAccount.platform as unknown) as SocialMediaConnectionProvider,
            username: pendingUnlinkAccount.link,
          },
        },
      });
    } catch (error) {
      showToast({
        message:
          (error as Error).message ||
          'Unable to unlink account. Please try again.',
        type: 'error',
      });
    } finally {
      setPendingUnlinkAccount(null);
    }
  };

  const renderLinkButton = (label: string) => (
    <Button
      variant="secondary"
      size="small"
      className={styles.linkButton}
      onClick={onLinkAccountConfirmation}
      loading={isConnecting}
      disabled={isActionDisabled}
    >
      {!isConnecting && (
        <>
          <MillionsIcon
            name="link"
            className={cn(styles.linkIcon, {
              [styles.linkIconLoading]: disabled,
            })}
          />
          {label}
        </>
      )}
    </Button>
  );

  return (
    <div className={styles.root}>
      <UnlinkSocialAccountDialog
        isOpen={isUnlinkDialogOpen}
        onClose={handleCloseUnlinkDialog}
        onConfirm={handleConfirmUnlink}
        loading={isUnlinking}
      />
      <SocialAuthorizationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={closeAuthorizationDialog}
        onAuthorize={handleAuthorize}
        socialPlatformName={mapSocialPlatformToLabel(socialAccount.platform)}
        loading={isAuthorizing}
      />
      <div className={styles.content}>
        <div>
          <MillionsIcon
            className={cn({ [styles.disabled]: disabled })}
            name={mapSocialPlatformToIcon(socialAccount.platform)}
          />
        </div>
        <div>
          <Text
            variant="subtitle2"
            className={cn({ [styles.disabled]: disabled })}
          >
            {mapSocialPlatformToLabel(socialAccount.platform)}
          </Text>
          <div className={styles.infoRow}>
            <Text
              variant="body2Regular"
              color="lights-low"
              className={cn({ [styles.disabled]: disabled })}
            >
              {socialAccount.link || 'Link account'}{' '}
              {socialAccount?.followersCount
                ? `● ${formatLargeNumber(
                    socialAccount?.followersCount
                  )} followers`
                : null}
            </Text>
            {showError && (
              <div className={styles.errorContainer}>
                <MillionsIcon name="close" className={styles.errorIcon} />
                <Text variant="body2Regular" className={styles.errorText}>
                  Please reconnect your account
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
      {socialAccount.isAdded || isLinked ? (
        showError ? (
          renderLinkButton('Reconnect')
        ) : (
          <Button
            onClick={handleRemoveLinkedSocialAccount}
            variant="secondary"
            size="small"
            loading={isUnlinking}
            disabled={isActionDisabled}
            loaderColor="error"
            aria-label="Unlink account"
          >
            {!isUnlinking && (
              <MillionsIcon
                name="trash"
                className={cn(styles.removeIcon, {
                  [styles.disabled]: disabled,
                })}
              />
            )}
          </Button>
        )
      ) : (
        renderLinkButton('Link')
      )}
    </div>
  );
};

export default SocialsRowWithIntegration;
